import React, { useMemo } from "react";
import { LineChart, Line, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChartLine, ChartBar } from "@phosphor-icons/react";

const COLORS = ["#FF3B30", "#007AFF", "#FFD60A", "#32D74B", "#FF9500", "#AF52DE"];

export function RPEEvolutionChart({ sessions }) {
  const { data, exercises } = useMemo(() => {
    // Build per-session average RPE by exercise
    const byDate = {};
    sessions.forEach((s) => {
      if (!s.sets || s.sets.length === 0) return;
      const date = (s.started_at || "").slice(0, 10);
      if (!byDate[date]) byDate[date] = {};
      // Group by exercise within this session
      const exAgg = {};
      s.sets.forEach((st) => {
        if (st.rpe == null) return;
        const key = st.exercise_name || "—";
        if (!exAgg[key]) exAgg[key] = { total: 0, count: 0 };
        exAgg[key].total += Number(st.rpe);
        exAgg[key].count += 1;
      });
      Object.entries(exAgg).forEach(([name, agg]) => {
        if (!byDate[date][name]) byDate[date][name] = { total: 0, count: 0 };
        byDate[date][name].total += agg.total;
        byDate[date][name].count += agg.count;
      });
    });

    const exSet = new Set();
    Object.values(byDate).forEach((d) => Object.keys(d).forEach((k) => exSet.add(k)));
    const exercises = Array.from(exSet).slice(0, 6);

    const rows = Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, exMap]) => {
        const row = { date };
        exercises.forEach((name) => {
          const v = exMap[name];
          row[name] = v ? +(v.total / v.count).toFixed(1) : null;
        });
        return row;
      });
    return { data: rows, exercises };
  }, [sessions]);

  if (data.length < 2) {
    return (
      <div className="card-base p-6" data-testid="rpe-chart-empty">
        <div className="flex items-center gap-2 mb-2">
          <ChartLine size={18} weight="bold" className="text-primary-app" />
          <h3 className="font-display font-bold uppercase text-lg tracking-tight">Evolución RPE por ejercicio</h3>
        </div>
        <p className="text-muted text-sm">Completá al menos 2 sesiones con RPE registrado para ver la evolución y detectar sobreentrenamiento.</p>
      </div>
    );
  }

  return (
    <div className="card-base p-6" data-testid="rpe-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ChartLine size={18} weight="bold" className="text-primary-app" />
          <h3 className="font-display font-bold uppercase text-lg tracking-tight">Evolución RPE por ejercicio</h3>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted">RPE ≥ 9 sostenido = riesgo de sobreentrenamiento</span>
      </div>
      <div className="w-full" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#262626" strokeDasharray="2 4" />
            <XAxis dataKey="date" stroke="#A3A3A3" tick={{ fontSize: 11 }} />
            <YAxis stroke="#A3A3A3" tick={{ fontSize: 11 }} domain={[0, 10]} />
            <Tooltip
              contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 0, color: "#fff" }}
              labelStyle={{ color: "#A3A3A3", textTransform: "uppercase", letterSpacing: 2, fontSize: 11 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {exercises.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function WeeklyLoadChart({ sessions }) {
  const { data, total, avgDaily } = useMemo(() => {
    const now = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      days.push({
        key: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString("es-AR", { weekday: "short" }).replace(".", "").toUpperCase(),
        load: 0,
      });
    }
    const map = Object.fromEntries(days.map((d) => [d.key, d]));

    sessions.forEach((s) => {
      const date = (s.started_at || "").slice(0, 10);
      if (!map[date]) return;
      (s.sets || []).forEach((st) => {
        const w = Number(st.weight_kg) || 0;
        const r = Number(st.reps) || 0;
        map[date].load += w * r; // each set counted once; sets table already has one row per set
      });
    });

    const total = days.reduce((a, d) => a + d.load, 0);
    const avgDaily = Math.round(total / 7);
    return { data: days, total, avgDaily };
  }, [sessions]);

  return (
    <div className="card-base p-6" data-testid="weekly-load-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ChartBar size={18} weight="bold" className="text-primary-app" />
          <h3 className="font-display font-bold uppercase text-lg tracking-tight">Carga semanal</h3>
        </div>
        <div className="text-xs text-muted uppercase tracking-widest flex gap-4">
          <span>Total: <span className="text-white font-bold">{total.toLocaleString()}</span> kg</span>
          <span>Promedio diario: <span className="text-white font-bold">{avgDaily.toLocaleString()}</span> kg</span>
        </div>
      </div>
      <div className="w-full" style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#262626" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="label" stroke="#A3A3A3" tick={{ fontSize: 11 }} />
            <YAxis stroke="#A3A3A3" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 0, color: "#fff" }}
              labelStyle={{ color: "#A3A3A3", textTransform: "uppercase", letterSpacing: 2, fontSize: 11 }}
              formatter={(v) => [`${v.toLocaleString()} kg`, "Volumen"]}
            />
            <Bar dataKey="load" fill="#FF3B30" radius={[0, 0, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted mt-3">Volumen = suma de (peso × reps) de todas las series registradas por día.</p>
    </div>
  );
}
